import { useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import AdminAuthPage from './pages/AdminAuthPage';
import StaffAuthPage from './pages/StaffAuthPage';
import StaffOnboardingPage from './pages/StaffOnboardingPage';
import DashboardPage from './pages/DashboardPage';
import CreateMeetingPage from './pages/CreateMeetingPage';
import MeetingDetailPage from './pages/MeetingDetailPage';
import EditMeetingPage from './pages/EditMeetingPage';
import CheckInPage from './pages/CheckInPage';
import ProjectorPage from './pages/ProjectorPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import RoomCalendarPage from './pages/RoomCalendarPage';
import NotFoundPage from './pages/NotFoundPage';
import AttendanceVerificationPage from './pages/AttendanceVerificationPage';
import { useStaffAuth } from './auth/StaffAuthContext';

function StaffRoute({ children, requireAdmin = false }: { children: React.ReactNode; requireAdmin?: boolean }) {
  const { identity, refreshProfile, signOut } = useStaffAuth();
  const location = useLocation();
  const [retrying, setRetrying] = useState(false);

  const returnTo = encodeURIComponent(location.pathname + location.search);
  if (identity.loading) return <div className="page-center"><div className="spinner" /></div>;
  if (!identity.isAuthenticated) return <Navigate to={`/staff-auth?returnTo=${returnTo}`} replace />;
  if (identity.profileError) return <main className="page-center"><section className="auth-card">
    <div className="eyebrow">Authenticated staff session</div>
    <h1>Profile unavailable</h1>
    <p className="muted">Your session is valid, but the profile service could not be loaded. Retry instead of creating another profile.</p>
    <p className="security-callout compact">{identity.profileError}</p>
    <div className="checkin-actions">
      <button className="button button-primary" disabled={retrying} onClick={() => { setRetrying(true); void refreshProfile().catch(() => undefined).finally(() => setRetrying(false)); }}>Retry profile</button>
      <button className="button button-secondary" disabled={retrying} onClick={() => { setRetrying(true); void signOut().catch(() => undefined).finally(() => setRetrying(false)); }}>Sign out</button>
    </div>
  </section></main>;
  if (!identity.hasProfile) return <Navigate to={`/staff-onboarding?returnTo=${returnTo}`} replace />;
  if (requireAdmin && identity.role !== 'Admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/staff-auth" element={<StaffAuthPage />} />
      <Route path="/staff-onboarding" element={<StaffOnboardingPage />} />
      <Route path="/admin-auth" element={<AdminAuthPage />} />
      <Route path="/dashboard" element={<StaffRoute><DashboardPage /></StaffRoute>} />
      <Route path="/profile" element={<StaffRoute><ProfilePage /></StaffRoute>} />
      <Route path="/meetings/new" element={<StaffRoute><CreateMeetingPage /></StaffRoute>} />
      <Route path="/rooms/calendar" element={<StaffRoute><RoomCalendarPage /></StaffRoute>} />
      <Route path="/meetings/:meetingId/edit" element={<StaffRoute><EditMeetingPage /></StaffRoute>} />
      <Route path="/meetings/:meetingId" element={<StaffRoute><MeetingDetailPage /></StaffRoute>} />
      <Route path="/checkin/:meetingId" element={<CheckInPage />} />
      <Route path="/projector/:meetingId" element={<ProjectorPage />} />
      <Route path="/attendance-verification/:token" element={<AttendanceVerificationPage />} />
      <Route path="/admin" element={<StaffRoute requireAdmin><AdminPage /></StaffRoute>} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
